import type { ReactNode } from 'react';
import { RotateCcw, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import { cn } from '@/lib/utils/cn';

export interface DateRangeValue {
  /** yyyy-MM-dd */
  from: string;
  /** yyyy-MM-dd */
  to: string;
}

interface DateRangeFilterProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  /** Shows an Apply button; omit when the page fetches on change. */
  onApply?: () => void;
  /** Shows a Reset button. */
  onReset?: () => void;
  applyLabel?: string;
  fromLabel?: string;
  toLabel?: string;
  /** Earliest / latest selectable date (yyyy-MM-dd). */
  min?: string;
  max?: string;
  disabled?: boolean;
  /** Extra filters (selects, search…) rendered after the dates, before the buttons. */
  children?: ReactNode;
  /** Extra buttons (Download, Export…) rendered after Filter / Reset. */
  actions?: ReactNode;
  /** Push `actions` to the right end of the bar (e.g. Export). */
  actionsAtEnd?: boolean;
  /** Smaller inputs and buttons for dashboard widgets and side panels. */
  compact?: boolean;
  /** Classes for the calendar popover, e.g. a higher z-index inside a modal. */
  calendarClassName?: string;
  /** Classes for the two date boxes instead of the default look (e.g. a page's own white input style). */
  inputClassName?: string;
  /**
   * "pill": one-line bar for toolbars (e.g. the agent dashboard's pill filter bar):
   * a single `label` on the left, small rounded inputs with "–" between, no Apply/Reset.
   */
  variant?: 'default' | 'pill';
  /** Label for the pill variant (e.g. "Scheduled"). */
  label?: string;
  className?: string;
  hideDates?: boolean;
}

// Extra buttons in `actions` get the same small size as Filter / Reset (wrapper takes no layout space).
const actionsButtonCls =
  '[&_button]:h-8 [&_button]:text-xs [&_button]:rounded-md [&_button_svg]:h-3.5 [&_button_svg]:w-3.5';

const pillInputCls = 'h-8 w-[7.75rem] rounded-full border border-border bg-muted/60 px-3 text-[11px]';

const labelCls = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground';
const inputStyle = { height: '40px', width: '170px' } as const;
const compactInputStyle = { height: '36px', width: '150px' } as const;

/**
 * Shared "From – To" date filter with optional Apply / Reset. Pair it with
 * `usePersistedState` / `usePersistedFilters` (lib/filters) so the range survives a
 * refresh and route changes until logout / switch.
 */
export function DateRangeFilter({
  value,
  onChange,
  onApply,
  onReset,
  applyLabel = 'Filter',
  fromLabel = 'From',
  toLabel = 'To',
  min,
  max,
  disabled,
  children,
  actions,
  actionsAtEnd = false,
  compact = false,
  calendarClassName,
  inputClassName,
  variant = 'default',
  label,
  className,
  hideDates = false,
}: DateRangeFilterProps) {
  const setFrom = (from: string) => onChange({ from, to: value.to && from > value.to ? from : value.to });
  const setTo = (to: string) => onChange({ from: value.from && to < value.from ? to : value.from, to });

  if (variant === 'pill') {
    return (
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        {label && (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
        )}
        <CalendarDateInput
          value={value.from}
          min={min}
          max={value.to || max}
          disabled={disabled}
          aria-label={`${label ? `${label} ` : ''}${fromLabel}`}
          className={pillInputCls}
          contentClassName={calendarClassName}
          onChange={setFrom}
        />
        <span className="text-[11px] text-muted-foreground">–</span>
        <CalendarDateInput
          value={value.to}
          min={value.from || min}
          max={max}
          disabled={disabled}
          aria-label={`${label ? `${label} ` : ''}${toLabel}`}
          className={pillInputCls}
          contentClassName={calendarClassName}
          onChange={setTo}
        />
        {children}
        {actions}
      </div>
    );
  }

  const style = compact ? compactInputStyle : inputStyle;
  return (
    <div className={cn('flex flex-wrap items-end gap-2', className)}>
      {!hideDates && (<>
      <div>
        <label className={labelCls}>{fromLabel}</label>
        <CalendarDateInput
          value={value.from}
          min={min}
          max={value.to || max}
          disabled={disabled}
          aria-label={fromLabel}
          className={inputClassName ?? 'form-control'}
          style={style}
          contentClassName={calendarClassName}
          onChange={setFrom}
        />
      </div>
      <div>
        <label className={labelCls}>{toLabel}</label>
        <CalendarDateInput
          value={value.to}
          min={value.from || min}
          max={max}
          disabled={disabled}
          aria-label={toLabel}
          className={inputClassName ?? 'form-control'}
          style={style}
          contentClassName={calendarClassName}
          onChange={setTo}
        />
      </div>
      </>)}
      {children}
      {/* Each button is its own item of the bar, so Filter stays on the filters' line
          when there is room and only the buttons that do not fit wrap. */}
      {onApply && (
        <Button type="button" size="sm" onClick={onApply} disabled={disabled}>
          <Search className="h-3.5 w-3.5" />
          {applyLabel}
        </Button>
      )}
      {onReset && (
        <Button type="button" variant="outline" size="sm" onClick={onReset} disabled={disabled}>
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </Button>
      )}
      {actions &&
        (actionsAtEnd ? (
          <div className={cn(actionsButtonCls, 'ml-auto flex flex-wrap gap-2')}>{actions}</div>
        ) : (
          <div className={cn(actionsButtonCls, 'contents')}>{actions}</div>
        ))}
    </div>
  );
}

/** Today as yyyy-MM-dd (local time). */
export function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** First day of the current month as yyyy-MM-dd (local time). */
export function firstOfMonthStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
